import React from 'react';

import _ from 'lodash';
import { HelpOutline } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';

import { OpenCloseMarker } from '@/app/components/OpenCloseMarker';
import { HighlightableJson } from '@/app/log-viewer/highlighting/HighlightableJson';
import { HighlightableString } from '@/app/log-viewer/highlighting/HighlightableString';
import {
  HighlightableStringExpandableContainer,
  HighlightableStringExpandableContainerState,
  toggleExpandingStateManually,
} from '@/app/log-viewer/highlighting/HighlightableStringExpandableContainer';
import { HighlightableStringView } from '@/app/log-viewer/highlighting/HighlightableStringView';

import { JsonViewer } from './JsonViewer';

export interface NetworkRequestBodyModelData extends HighlightableStringExpandableContainer {
  title: HighlightableString;

  // Should be undefined if json is defined.
  notJson?: {
    string: HighlightableString;
  };

  json?: {
    originalJson: any;

    jqQuery: string;

    processedJqResult: {
      jqQuery: string;
      processedHighlightableJson: HighlightableJson;
    };
  };
}

type Props = {
  data: DeepReadonly<NetworkRequestBodyModelData>;

  onDataChange: (args: {
    change: DeepPartial<NetworkRequestBodyModelData>;
    postProcess: boolean;
  }) => void;
  onRequestCreateNewColumn: (jqPath: string) => void;
};

export class NetworkRequestBody extends React.Component<Props> {
  render() {
    const data = this.props.data;

    return (
      <div>
        <div
          className="details-header"
          onClick={() => {
            this.props.onDataChange({
              change: {
                expandingState: toggleExpandingStateManually(data.expandingState),
              },
              postProcess: false,
            });
          }}
        >
          <OpenCloseMarker
            open={data.expandingState !== HighlightableStringExpandableContainerState.Collapsed}
          />
          &nbsp;
          <HighlightableStringView string={data.title} />
        </div>

        {data.expandingState !== HighlightableStringExpandableContainerState.Collapsed ? (
          <div>
            {data.json ? (
              <>
                <span className="jq-container">
                  <input
                    className="jq-body-input"
                    type="text"
                    defaultValue={data.json.jqQuery}
                    placeholder="Enter jq query..."
                    onChange={_.debounce((e) => {
                      this.onJqQueryChange(e.target.value);
                    }, 300)}
                  />

                  <Tooltip title="jq syntax reference">
                    <IconButton
                      size="small"
                      onClick={() => {
                        this.openJqHelp();
                      }}
                    >
                      <HelpOutline />
                    </IconButton>
                  </Tooltip>
                </span>

                <JsonViewer
                  highlightableJson={data.json.processedJqResult.processedHighlightableJson}
                  onDataChange={(jsonChange) => {
                    const change: DeepPartial<NetworkRequestBodyModelData> = {
                      json: {
                        processedJqResult: {
                          processedHighlightableJson: jsonChange,
                        },
                      },
                    };
                    this.props.onDataChange({
                      change,
                      postProcess: false,
                    });
                  }}
                  onCopyJqPath={(p) => {
                    const jqPath = data.json!.jqQuery + p;
                    navigator.clipboard.writeText(jqPath);
                  }}
                  onExtractNewColumn={(p) => {
                    const jqPath = data.json!.jqQuery + p;
                    const c = this.props.onRequestCreateNewColumn;
                    if (c) {
                      c(jqPath);
                    }
                  }}
                />
              </>
            ) : (
              <div className="verbatim-multiline">
                <HighlightableStringView string={data.notJson!.string} />
              </div>
            )}
          </div>
        ) : null}
      </div>
    );
  }

  private openJqHelp() {
    window
      .open('https://github.com/kantord/jq-in-the-browser#supported-features', '_blank')
      ?.focus();
  }

  private onJqQueryChange(query: string) {
    this.props.onDataChange({
      change: {
        json: {
          jqQuery: query,
        },
      },
      postProcess: true,
    });
  }
}
